<template>
  <div ref="reviewObserverTarget">
    <div
      class="flex flex-col items-start justify-between gap-32 md:flex-row"
      data-qa="user-comment"
    >
      <div class="flex items-start">
        <RevAvatar
          class="mr-12 shrink-0"
          :class="avatarBackground"
          :label="avatarLabel"
          size="medium"
          :thumbnail="authIconAvatar"
        />

        <div class="flex-1">
          <p class="body-1-bold">
            {{ userName }}
          </p>

          <p class="body-2">
            {{ commentDate }}
          </p>
        </div>
      </div>
      <RevRating :score="averageRate" size="small" />
    </div>
    <div class="relative mt-32">
      <span class="body-1 whitespace-pre-line">
        {{ displayedText }}
      </span>

      <RevLink v-if="isCommentLong" class="ml-16" @click="onToggleText">
        {{ toggleTextLabel }}
      </RevLink>
    </div>

    <div
      v-if="isTranslatable"
      class="mt-24 flex flex-col flex-wrap items-start md:mt-32 md:flex-row md:items-center"
    >
      <div v-if="isTranslated" class="mb-8 mr-4 flex items-center md:mb-0">
        <IconSparkles class="mr-4" />

        {{ translatedFrom }}

        <span class="ml-4 hidden md:block"> - </span>
      </div>

      <RevLink @click="onToggleTranslation">
        {{ toggleTranslationLabel }}
      </RevLink>
    </div>

    <RevButtonBase v-if="product.title" class="mt-32 block" :to="product.link">
      <RevTag :label="product.title" variant="secondary" />
    </RevButtonBase>

    <p v-if="purchaseDate" class="body-2 mt-32">
      {{ purchaseDate }}
    </p>
  </div>
</template>

<script lang="ts" setup>
import { useAvatar, useRoute } from '#imports'
import { computed, onBeforeUnmount, onMounted, ref, toRef } from 'vue'

import type { Review } from '@backmarket/http-api/src/api-specs-reviews/types/review'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevAvatar } from '@ds/components/Avatar'
import { RevButtonBase } from '@ds/components/ButtonBase'
import { RevLink } from '@ds/components/Link'
import { RevRating } from '@ds/components/Rating'
import { RevTag } from '@ds/components/Tag'
import { IconSparkles } from '@ds/icons/IconSparkles'

import { TRACKING_PAGE_TYPE_BY_ROUTE_NAME } from '~/constants/trackingPageType'

import { MAX_COMMENT_LENGTH } from '../../constants'

import translations from './UserComment.translations'
import { useFormatDateToLongDate } from './use-format-to-long-date'
import { useGetTranslatableComment } from './useGetTranslatableComment'

const props = defineProps<Review & { position: number }>()

const i18n = useI18n()
const route = useRoute()
const { trackClick, trackReviewImpression } = useTracking()
const {
  market: { countryCode: currentCountryCode },
} = useMarketplace()
const {
  label: avatarLabel,
  thumbnail: authIconAvatar,
  backgroundColor: avatarBackground,
} = useAvatar(toRef(props.customer, 'firstName'))
const {
  comment,
  isTranslatable,
  isTranslated,
  toggleTranslation,
  toggleTranslationLabel,
  translatedFrom,
} = useGetTranslatableComment(props)

const observer = ref<IntersectionObserver>()
const reviewObserverTarget = ref<null | HTMLElement>(null)
const cutComment = ref(true)

const userName = computed(() => {
  if (props.customer.firstName && props.customer.lastName) {
    return `${props.customer.firstName} ${props.customer.lastName}`
  }

  return props.customer.firstName ?? props.customer.lastName ?? ''
})

const commentDate = computed(() => {
  return i18n(translations.reviewDate, {
    country: i18n.country(props.countryCode),
    date: useFormatDateToLongDate(props.createdAt),
  })
})

const purchaseDate = computed(() =>
  props.product.purchasedAt
    ? i18n(translations.purchaseDate, {
        date: useFormatDateToLongDate(props.product.purchasedAt),
      })
    : '',
)

const isCommentLong = computed(() => comment.value.length > MAX_COMMENT_LENGTH)

const displayedText = computed(() => {
  if (isCommentLong.value && cutComment.value) {
    return `${comment.value.substring(0, MAX_COMMENT_LENGTH)}...`
  }

  return comment.value
})

const toggleTextLabel = computed(() =>
  cutComment.value ? i18n(translations.showMore) : i18n(translations.showLess),
)

const isReviewFromCurrentCountry = computed(
  () => props.countryCode === currentCountryCode,
)

function onToggleText() {
  trackClick({
    name: cutComment.value ? 'see_more' : 'see_less',
    zone: `review_position_${props.position}`,
    page_type: route.name as string,
  })

  cutComment.value = !cutComment.value
}

async function onToggleTranslation() {
  trackClick({
    name: isTranslated.value ? 'see_original' : 'translate',
    zone: `review_position_${props.position}`,
    page_type: route.name as string,
  })

  await toggleTranslation()
  if (isTranslated.value) {
    cutComment.value = false
  }
}

function handleIntersection(entries: IntersectionObserverEntry[]) {
  const target = entries[0]

  const routeName = route.name?.toString() || ''

  if (target.isIntersecting) {
    const { position, averageRate, countryCode, product } = props
    trackReviewImpression({
      ...product,
      averageRate,
      countryCode,
      isReviewFromCurrentCountry: isReviewFromCurrentCountry.value,
      isReviewTranslated: isTranslated.value,
      pageType: TRACKING_PAGE_TYPE_BY_ROUTE_NAME[routeName],
      reviewPosition: position,
    })

    observer.value?.disconnect()
  }
}

onMounted(async () => {
  observer.value = new IntersectionObserver(handleIntersection)

  if (reviewObserverTarget.value)
    observer.value.observe(reviewObserverTarget.value)
})

onBeforeUnmount(() => {
  observer.value?.disconnect()
})
</script>
